import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { TeamBadge } from "@/components/TeamBadge";
import { useI18n } from "@/context/I18nContext";
import { Trophy } from "lucide-react";

function ScoreInput({ value, onChange, onSave, testid }) {
  return (
    <input type="number" min="0" max="30" value={value ?? ""} data-testid={testid}
      onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Math.min(30, parseInt(e.target.value) || 0)))}
      onBlur={onSave}
      className="w-10 h-9 text-center bg-secondary border border-border font-display text-lg focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary" />
  );
}

function TieCard({ tie, inputs, setField, save }) {
  const { t } = useI18n();
  const { teamA, teamB, tie_id, legs, winner_id, aggA, aggB } = tie;
  const known = teamA && teamB;
  const v = inputs[tie_id] || {};
  const cls = (team) => team && winner_id === team.team_id ? "text-primary font-bold" : "text-foreground";

  return (
    <div className="bg-card border border-border p-3 w-60" data-testid={`ko-tie-${tie_id}`}>
      {!known ? (
        <div className="h-[92px] flex items-center justify-center text-muted-foreground text-sm">{t("ko.tbd")}</div>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <TeamBadge team={teamA} size={20} />
              <span className={`text-sm truncate ${cls(teamA)}`}>{teamA.short}</span>
            </div>
            <span className="text-[10px] text-muted-foreground">{aggA != null ? `${t("ko.agg")} ${aggA}` : ""}</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <TeamBadge team={teamB} size={20} />
              <span className={`text-sm truncate ${cls(teamB)}`}>{teamB.short}</span>
            </div>
            <span className="text-[10px] text-muted-foreground">{aggB != null ? `${t("ko.agg")} ${aggB}` : ""}</span>
          </div>

          <div className="border-t border-border pt-2 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-10">{t("ko.leg1")}</span>
              <span className="text-xs flex-1 text-right truncate">{teamA.short}</span>
              <ScoreInput value={v.leg1_home} testid={`ko-l1h-${tie_id}`} onChange={(x) => setField(tie_id, "leg1_home", x)} onSave={() => save(tie_id)} />
              <span className="text-muted-foreground text-xs">-</span>
              <ScoreInput value={v.leg1_away} testid={`ko-l1a-${tie_id}`} onChange={(x) => setField(tie_id, "leg1_away", x)} onSave={() => save(tie_id)} />
              <span className="text-xs flex-1 truncate">{teamB.short}</span>
            </div>
            {legs === 2 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-10">{t("ko.leg2")}</span>
                <span className="text-xs flex-1 text-right truncate">{teamB.short}</span>
                <ScoreInput value={v.leg2_home} testid={`ko-l2h-${tie_id}`} onChange={(x) => setField(tie_id, "leg2_home", x)} onSave={() => save(tie_id)} />
                <span className="text-muted-foreground text-xs">-</span>
                <ScoreInput value={v.leg2_away} testid={`ko-l2a-${tie_id}`} onChange={(x) => setField(tie_id, "leg2_away", x)} onSave={() => save(tie_id)} />
                <span className="text-xs flex-1 truncate">{teamA.short}</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export function KnockoutBracket({ leagueId }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState({});
  const timers = useRef({});

  const load = async () => {
    const { data } = await api.get(`/leagues/${leagueId}/knockout`);
    setData(data);
    const init = {};
    data.rounds.forEach((r) => r.ties.forEach((tie) => {
      const p = tie.pred || {};
      init[tie.tie_id] = { leg1_home: p.leg1_home, leg1_away: p.leg1_away, leg2_home: p.leg2_home, leg2_away: p.leg2_away };
    }));
    setInputs(init);
  };

  useEffect(() => { load(); }, [leagueId]);

  const setField = (tieId, field, val) =>
    setInputs((s) => ({ ...s, [tieId]: { ...(s[tieId] || {}), [field]: val } }));

  const save = (tieId) => {
    // debounce per tie: coalesce rapid leg edits into one save + one cascade reload
    clearTimeout(timers.current[tieId]);
    timers.current[tieId] = setTimeout(async () => {
      const v = inputs[tieId] || {};
      await api.put(`/leagues/${leagueId}/knockout/${tieId}`, {
        leg1_home: v.leg1_home ?? null, leg1_away: v.leg1_away ?? null,
        leg2_home: v.leg2_home ?? null, leg2_away: v.leg2_away ?? null,
      });
      await load();
    }, 600);
  };

  if (!data) return null;
  const champion = data.champion;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="text-muted-foreground text-sm">{t("ko.hint")}</p>
        {champion && (
          <div className="flex items-center gap-2 border border-primary bg-primary/10 px-4 py-2" data-testid="ko-champion">
            <Trophy size={16} className="text-primary" />
            <span className="overline text-primary">{t("ko.champion")}</span>
            <TeamBadge team={champion} size={22} />
            <span className="font-display text-lg">{champion.name}</span>
          </div>
        )}
      </div>

      <div className="overflow-x-auto pb-4">
        <div className="flex gap-6 min-w-max">
          {data.rounds.map((r) => (
            <div key={r.id} className="flex flex-col gap-3" data-testid={`ko-round-${r.id}`}>
              <p className="overline text-primary sticky left-0">{r.name}</p>
              <div className="flex flex-col gap-3 justify-around h-full">
                {r.ties.map((tie) => (
                  <TieCard key={tie.tie_id} tie={tie} inputs={inputs} setField={setField} save={save} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
